import React, { useMemo, useState } from 'react';

import { css, StyleSheet } from 'aphrodite';
import { Alert, Button, Form } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';

import Container from 'components/shared/Container';
import { DATA_SOURCE_DASHBOARD_SUMMARY } from 'lib/networking/endpoints';
import { DATA_SOURCES_VIEW_TABS } from 'lib/constants';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import useGet from 'lib/hooks/useGet';

import DataSourcesDashboardTable from './DataSourcesDashboardTable';
import SummaryTile, { TILE_FILTERS } from './DataSourcesDashboardSummaryTile';

const TIME_RANGE_OPTIONS = [
  { label: 'Last 1 week', lookbackDays: 7 },
  { label: 'Last 2 weeks', lookbackDays: 14 },
  { label: 'Last 3 weeks', lookbackDays: 21 },
  { label: 'Last 1 month', lookbackDays: 30 },
  { label: 'Last 3 months', lookbackDays: 90 },
];

function matchesFilter(source, activeFilter) {
  switch (activeFilter) {
    case TILE_FILTERS.FAILED:
      return source.failed_runs > 0;
    case TILE_FILTERS.FULLY_FAILED:
      return source.is_fully_failed;
    case TILE_FILTERS.RUNNING:
      return source.is_running;
    case TILE_FILTERS.EXECUTED:
      return source.total_runs > 0;
    default:
      return true;
  }
}

function DataSourcesDashboardView() {
  const history = useHistory();
  const [lookbackDays, setLookbackDays] = useState(TIME_RANGE_OPTIONS[0].lookbackDays);
  const [activeFilter, setActiveFilter] = useState(null);

  const {
    data: { summary, sources = [] } = {},
    loading,
    error,
    refetch,
  } = useGet(DATA_SOURCE_DASHBOARD_SUMMARY, {
    lookback_days: lookbackDays,
  });

  const filteredSources = useMemo(
    () => sources.filter(source => matchesFilter(source, activeFilter)),
    [sources, activeFilter],
  );

  function toggleFilter(filter) {
    setActiveFilter(activeFilter === filter ? null : filter);
  }

  function handleRowClick(source) {
    history.push(
      `/data-sources/${source.id}?tab=${encodeURIComponent(DATA_SOURCES_VIEW_TABS.PROCESS_LOGS)}`,
    );
  }

  return (
    <Container>
      <div className={css(styles.header)}>
        <h3 className="font-weight-bold">Data Sources Dashboard</h3>
        <Form.Group className={css(styles.timeRangeGroup)}>
          <Form.Control
            as="select"
            value={lookbackDays}
            onChange={e => setLookbackDays(parseInt(e.target.value, 10))}
          >
            {TIME_RANGE_OPTIONS.map(option => (
              <option key={option.lookbackDays} value={option.lookbackDays}>
                {option.label}
              </option>
            ))}
          </Form.Control>
        </Form.Group>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <Alert variant="danger">
          Failed to load the data sources dashboard
          {error?.response?.data?.message ? `: ${error.response.data.message}` : '.'}{' '}
          <Button variant="link" className="p-0 align-baseline" onClick={() => refetch()}>
            Retry
          </Button>
        </Alert>
      ) : !summary ? (
        <Alert variant="warning">
          No dashboard data available.{' '}
          <Button variant="link" className="p-0 align-baseline" onClick={() => refetch()}>
            Retry
          </Button>
        </Alert>
      ) : (
        <>
          <div className={css(styles.tileRow)}>
            <SummaryTile
              filterKey={TILE_FILTERS.EXECUTED}
              count={summary.executed_sources}
              isActive={activeFilter === TILE_FILTERS.EXECUTED}
              onClick={() => toggleFilter(TILE_FILTERS.EXECUTED)}
            />
            <SummaryTile
              filterKey={TILE_FILTERS.FAILED}
              count={summary.failed_runs}
              isActive={activeFilter === TILE_FILTERS.FAILED}
              onClick={() => toggleFilter(TILE_FILTERS.FAILED)}
            />
            <SummaryTile
              filterKey={TILE_FILTERS.FULLY_FAILED}
              count={summary.fully_failed_sources}
              isActive={activeFilter === TILE_FILTERS.FULLY_FAILED}
              onClick={() => toggleFilter(TILE_FILTERS.FULLY_FAILED)}
            />
            <SummaryTile
              filterKey={TILE_FILTERS.RUNNING}
              count={summary.running}
              isActive={activeFilter === TILE_FILTERS.RUNNING}
              onClick={() => toggleFilter(TILE_FILTERS.RUNNING)}
            />
          </div>

          <DataSourcesDashboardTable sources={filteredSources} onRowClick={handleRowClick} />
        </>
      )}
    </Container>
  );
}

const styles = StyleSheet.create({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  timeRangeGroup: {
    marginBottom: 0,
    minWidth: 180,
  },
  tileRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    marginBottom: 24,
  },
});

export default DataSourcesDashboardView;
